import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Plus,
  LayoutTemplate,
  Pencil,
  Trash2,
  ExternalLink,
  Copy,
  Check,
  Eye,
  Lock,
} from "lucide-react";
import { LandingPage } from "../../../types";
import { useAppServices } from "../../../hook/services";
import { useMarketplace } from "../../../marketplace/context";
import { useFeature } from "../../../marketplace/FeatureGate";
import { buildPageUrl, copyToClipboard, formatNumber } from "../../../utils";
import HeroSection from "../../../components/ui/HeroSection";
import Button from "../../../components/ui/Button";
import Badge from "../../../components/ui/Badge";
import EmptyState from "../../../components/ui/EmptyState";
import Skeleton from "../../../components/ui/Skeleton";
import { useConfirm } from "../../../components/ui/ConfirmDialog";
import TemplatePicker from "./TemplatePicker";
import { PageTemplate } from "./templates";

const Pages = () => {
  const navigate = useNavigate();
  const services = useAppServices();
  const confirm = useConfirm();
  const { buildPath, openUpgrade } = useMarketplace();
  const { allowed, minTierLabel } = useFeature("landingPages");
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const startWith = (tpl: PageTemplate | null) => {
    setPickerOpen(false);
    navigate(buildPath("landing-pages/new"), tpl ? { state: { template: tpl.data } } : undefined);
  };

  const fetchPages = useCallback(async () => {
    setLoading(true);
    const { response } = await services.landing_pages.get();
    if (Array.isArray(response)) setPages(response);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const remove = async (p: LandingPage) => {
    const ok = await confirm({
      title: `Delete "${p.title}"?`,
      message: "The public page will stop working immediately.",
      confirmLabel: "Delete page",
    });
    if (!ok) return;
    await services.landing_pages.delete({ query: `_id=${p._id}` });
    setPages((curr) => curr.filter((x) => x._id !== p._id));
  };

  const copy = async (p: LandingPage) => {
    if (await copyToClipboard(buildPageUrl(p.slug))) {
      setCopiedId(p._id);
      setTimeout(() => setCopiedId(null), 1500);
    }
  };

  // Plan gate: landing pages require Growth+.
  if (!allowed) {
    return (
      <div className="space-y-6">
        <HeroSection
          eyebrow={
            <>
              <LayoutTemplate size={11} className="text-brand-500" />
              <span>Pages</span>
            </>
          }
          title="Landing pages"
          description="Build a hosted link-in-bio page — a bio, avatar, and a list of links at one shareable URL."
        />
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card">
          <EmptyState
            icon={<Lock size={26} />}
            tone="amber"
            title={`Landing pages are a ${minTierLabel} feature`}
            description="Upgrade your plan to build hosted link-in-bio pages with templates, custom themes and analytics."
            action={
              <Button variant="gradient" onClick={openUpgrade}>
                Upgrade to {minTierLabel}
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HeroSection
        eyebrow={
          <>
            <LayoutTemplate size={11} className="text-brand-500" />
            <span>Pages</span>
          </>
        }
        title="Landing pages"
        description="Build a hosted link-in-bio page — a bio, avatar, and a list of links at one shareable URL."
        action={
          <Button
            variant="gradient"
            size="lg"
            onClick={() => setPickerOpen(true)}
            icon={<Plus size={15} strokeWidth={2.5} />}
          >
            New page
          </Button>
        }
      />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-[150px]" rounded="rounded-2xl" />)}
        </div>
      ) : pages.length === 0 ? (
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card">
          <EmptyState
            icon={<LayoutTemplate size={26} />}
            tone="brand"
            title="No landing pages yet"
            description="Create a link-in-bio page to share all your links from one place."
            action={
              <Button variant="gradient" onClick={() => setPickerOpen(true)} icon={<Plus size={14} strokeWidth={2.5} />}>
                Create a page
              </Button>
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 anim-in">
          {pages.map((p) => (
            <div key={p._id} className="group relative bg-white border border-ink-200/70 rounded-2xl shadow-card hover:shadow-card-hover transition-all overflow-hidden">
              <div className="h-1.5 w-full" style={{ background: p.design?.button_color || "#6366f1" }} />
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-display font-bold shrink-0" style={{ background: p.avatar_color }}>
                    {(p.title || "?").charAt(0).toUpperCase()}
                  </span>
                  <Badge tone={p.published ? "emerald" : "neutral"} dot={p.published}>
                    {p.published ? "Published" : "Draft"}
                  </Badge>
                </div>
                <h3 className="mt-3 font-display font-semibold text-ink-900 text-base tracking-tight truncate">{p.title}</h3>
                <button onClick={() => copy(p)} className="mt-1 inline-flex items-center gap-1.5 text-xs text-brand-600 hover:text-brand-700 font-mono" title={buildPageUrl(p.slug)}>
                  {copiedId === p._id ? <Check size={12} /> : <Copy size={12} />}
                  {copiedId === p._id ? "Copied" : `/p/${p.slug}`}
                </button>
                <div className="mt-3 flex items-center gap-3 text-xs text-ink-500">
                  <span className="inline-flex items-center gap-1"><Eye size={13} className="text-ink-400" /> {formatNumber(p.views)} views</span>
                  <span className="text-ink-300">·</span>
                  <span>{p.blocks?.length || 0} blocks</span>
                </div>
              </div>

              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                <a href={buildPageUrl(p.slug)} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-md bg-white/90 backdrop-blur ring-1 ring-ink-200 text-ink-500 hover:text-ink-900 flex items-center justify-center shadow-sm" title="Open">
                  <ExternalLink size={13} />
                </a>
                <RouterLink to={buildPath(`landing-pages/${p._id}`)} className="w-7 h-7 rounded-md bg-white/90 backdrop-blur ring-1 ring-ink-200 text-ink-500 hover:text-ink-900 flex items-center justify-center shadow-sm" title="Edit">
                  <Pencil size={13} />
                </RouterLink>
                <button onClick={() => remove(p)} className="w-7 h-7 rounded-md bg-white/90 backdrop-blur ring-1 ring-ink-200 text-ink-500 hover:text-rose-600 flex items-center justify-center shadow-sm" title="Delete">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <TemplatePicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={startWith} />
    </div>
  );
};

export default Pages;
